/* Pyjama Cinematic — WebGL hero engine.
   The engine methods below are ported verbatim from the original prototype (Atelier.dc.html);
   only the mount points are adapted: React refs -> DOM elements via a {current} shim, the scroll
   source -> window/document, and model URLs -> Shopify theme asset URLs (data-* attributes).
   window.THREE / window.__GLTFLoader are provided by the module bootstrap in sections/hero.liquid. */
(function () {
  'use strict';

  class HeroVoyage {
    constructor(root) {
      this.root = root;
      var css = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
      this.accent = css || '#8a5cff';
      this.reduce = false;
      try { this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
      this.isMobile = (typeof window !== 'undefined') && (window.innerWidth < 820 || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || ''));
      this.renderP = 0; this.targetP = 0; this.lastP = 0; this.maxScroll = 1;
      this.disposed = false; this._tries = 0; this.t = 0;

      var q = function (s) { return root.querySelector(s); };
      // ref shim: engine reads this.xxxRef.current — wire each to a live DOM element
      this.canvasRef    = { current: q('[data-hero-canvas]') };
      this.stageRef     = { current: q('[data-hero-stage]') };
      this.heroTrackRef = { current: q('[data-hero-track]') };
      this.volRef       = { current: q('[data-hero-vol]') };
      this.s1Ref        = { current: q('[data-hero-scene="0"]') };
      this.s2Ref        = { current: q('[data-hero-scene="1"]') };
      this.s3Ref        = { current: q('[data-hero-scene="2"]') };
      this.hintRef      = { current: q('[data-hero-hint]') };
      this.progRef      = { current: q('[data-hero-prog]') };
      this.fallbackRef  = { current: q('[data-hero-fallback]') };
      // page scroll source (window scroll); scrollTop/clientHeight read from the scrolling element
      this.rootRef      = { current: document.scrollingElement || document.documentElement };

      // Shopify asset URLs for the balanced GLB models (from section data attributes)
      this._modelUrls = { pajama: root.getAttribute('data-pajama-url') || '', dress: root.getAttribute('data-dress-url') || '' };

      this.onScroll = function () {
        var sc = this.rootRef.current;
        this.targetP = (sc && this.maxScroll > 0) ? sc.scrollTop / this.maxScroll : 0;
        if (document.hidden && this.ready) { this.renderP = this.targetP; try { this.frame(0.016); } catch (e) {} }
      }.bind(this);
      this.onResize = function () { this.resize(); }.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.waitForThree();
    }

  waitForThree() {
    if (this.disposed) return;
    if (window.THREE && window.__GLTFLoader) { try { this.init(); } catch (e) { console.error('init error', e); if (this.fallbackRef.current) this.fallbackRef.current.style.display = 'flex'; } return; }
    if (++this._tries > 200) { if (this.fallbackRef.current) this.fallbackRef.current.style.display = 'flex'; return; }
    setTimeout(() => this.waitForThree(), 60);
  }
  hexToRgb(h) { h = (h || '#8a5cff').replace('#', ''); if (h.length === 3) h = h.split('').map(c => c + c).join(''); const n = parseInt(h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
  S(a, b, x) { x = (x - a) / (b - a); x = Math.max(0, Math.min(1, x)); return x * x * (3 - 2 * x); }
  bell(x) { return Math.sin(Math.max(0, Math.min(1, x)) * Math.PI); }
  clamp(x, a, b) { return Math.max(a, Math.min(b, x)); }
  mix3(c1, c2, c3, t) { let a, b, k; if (t < 0.5) { a = c1; b = c2; k = t / 0.5; } else { a = c2; b = c3; k = (t - 0.5) / 0.5; } return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k]; }
  profileR(pts, t) {
    for (let i = 0; i < pts.length - 1; i++) {
      if (t <= pts[i + 1][0]) { const k = (t - pts[i][0]) / (pts[i + 1][0] - pts[i][0] || 1); const kk = k * k * (3 - 2 * k); return pts[i][1] + (pts[i + 1][1] - pts[i][1]) * kk; }
    }
    return pts[pts.length - 1][1];
  }
  gradCanvas(cfg) {
    const c = document.createElement('canvas'); c.width = 1024; c.height = 512; const x = c.getContext('2d');
    x.fillStyle = cfg.base; x.fillRect(0, 0, 1024, 512);
    cfg.blobs.forEach(b => { const g = x.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r); g.addColorStop(0, b.c); g.addColorStop(1, 'rgba(0,0,0,0)'); x.fillStyle = g; x.fillRect(0, 0, 1024, 512); });
    return c;
  }
  softSprite(col) {
    const c = document.createElement('canvas'); c.width = 256; c.height = 256; const x = c.getContext('2d');
    const g = x.createRadialGradient(128, 128, 0, 128, 128, 128);
    g.addColorStop(0, col); g.addColorStop(0.4, col.replace(/[\d.]+\)$/, '0.32)')); g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g; x.fillRect(0, 0, 256, 256); return c;
  }

  // ---------- init ----------
  init() {
    const T = window.THREE;
    const cv = this.canvasRef.current; if (!cv) return;
    let renderer;
    try { renderer = new T.WebGLRenderer({ canvas: cv, antialias: true, alpha: false, powerPreference: 'high-performance' }); }
    catch (e) { if (this.fallbackRef.current) this.fallbackRef.current.style.display = 'flex'; return; }
    this.T = T; this.renderer = renderer;
    renderer.setClearColor(0x04030a, 1);
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.32;
    renderer.outputColorSpace = T.SRGBColorSpace;

    const scene = new T.Scene();
    scene.fog = new T.FogExp2(0x06040f, 0.01);
    this.scene = scene;

    const cam = new T.PerspectiveCamera(42, 1, 0.1, 500);
    cam.position.set(0, 0.4, 9.6);
    this.cam = cam;

    const bgTex = new T.CanvasTexture(this.gradCanvas({ base: '#05030d', blobs: [
      { x: 512, y: 300, r: 460, c: 'rgba(72,44,150,0.85)' },
      { x: 300, y: 160, r: 300, c: 'rgba(120,70,210,0.4)' },
      { x: 760, y: 360, r: 320, c: 'rgba(40,30,90,0.5)' } ] }));
    bgTex.mapping = T.EquirectangularReflectionMapping; bgTex.colorSpace = T.SRGBColorSpace;
    scene.background = bgTex;

    const envTex = new T.CanvasTexture(this.gradCanvas({ base: '#14103a', blobs: [
      { x: 300, y: 130, r: 260, c: 'rgba(255,255,255,1)' },
      { x: 730, y: 180, r: 240, c: 'rgba(200,150,255,1)' },
      { x: 520, y: 400, r: 300, c: 'rgba(150,120,255,0.95)' },
      { x: 110, y: 380, r: 220, c: 'rgba(255,200,235,0.95)' },
      { x: 880, y: 430, r: 200, c: 'rgba(120,160,255,0.9)' } ] }));
    envTex.mapping = T.EquirectangularReflectionMapping;
    const pmrem = new T.PMREMGenerator(renderer);
    this.envRT = pmrem.fromEquirectangular(envTex);
    scene.environment = this.envRT.texture;
    pmrem.dispose(); envTex.dispose();

    scene.add(new T.AmbientLight(0x442f7a, 0.6));
    this.keyLight = new T.PointLight(0x9a6bff, 90, 0, 2); this.keyLight.position.set(5, 6, 5); scene.add(this.keyLight);
    this.rimLight = new T.PointLight(0x6fa0ff, 70, 0, 2); this.rimLight.position.set(-6, 2, -5); scene.add(this.rimLight);
    this.fillLight = new T.DirectionalLight(0xffffff, 0.5); this.fillLight.position.set(0, 4, 8); scene.add(this.fillLight);
    this.backLight = new T.PointLight(0xffffff, 55, 0, 2); this.backLight.position.set(-2, 1, -6); scene.add(this.backLight);

    this.glassMat = new T.MeshPhysicalMaterial({
      color: new T.Color(0xf0ecff), metalness: 0, roughness: 0.035,
      transmission: 0.97, thickness: 0.7, ior: 1.47,
      attenuationColor: new T.Color(0xac86ff), attenuationDistance: 4.5,
      clearcoat: 1, clearcoatRoughness: 0.06, iridescence: 0.7, iridescenceIOR: 1.32,
      emissive: new T.Color(0x1c1148), emissiveIntensity: 0.5,
      specularIntensity: 1.4, envMapIntensity: 3.2, side: T.DoubleSide, transparent: true, opacity: 1
    });

    this.buildGarments();
    this.buildTParticles();
    this.buildCrystal();
    this.buildCore();
    this.loadModels();
    this.buildRibbons();
    this.buildStars();
    this.buildParticles();
    this.buildNebula();

    // body silhouettes (closed ends => solid glass): t=0 hem .. t=1 neck
    // pajama SHIRT flat-lay: rounded hip-length hem, soft waist, wide dropped shoulders, deep collar pinch
    this.pA = [[0,0.05],[0.04,0.52],[0.14,0.53],[0.48,0.49],[0.68,0.55],[0.82,0.62],[0.9,0.66],[0.95,0.4],[1,0.06]];
    // fitted ribbed nightdress: body-hugging, gentle bust, straight fitted, short
    this.pB = [[0,0.02],[0.05,0.36],[0.24,0.37],[0.46,0.35],[0.64,0.37],[0.8,0.4],[0.92,0.41],[1,0.02]];
    // Others gown: dramatic flared trumpet
    this.pC = [[0,0.02],[0.08,0.55],[0.2,0.85],[0.34,0.42],[0.55,0.3],[0.75,0.34],[0.9,0.42],[1,0.02]];
    // per-scene cross-section flatten (front-back depth vs width). Pajama very flat => flat-lay garment with side seams.
    this.flatA = 0.34; this.flatB = 0.58; this.flatC = 0.72;
    this.byA = [-0.2, 1.26]; this.byB = [-0.62, 1.08]; this.byC = [-1.9, 1.22];

    this.c1 = [138, 92, 255]; this.c2 = [224, 132, 196]; this.c3 = [104, 122, 255];

    this.clock = new T.Clock();
    this.ready = true;
    this.resize(); this.onScroll();
    this.bindSpinner();
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }

  makeTube(R, C, mat) {
    const T = this.T;
    const pos = new Float32Array(R * C * 3);
    const idx = [];
    for (let i = 0; i < R - 1; i++) for (let j = 0; j < C; j++) {
      const a = i * C + j, b = i * C + ((j + 1) % C), c = (i + 1) * C + j, d = (i + 1) * C + ((j + 1) % C);
      idx.push(a, c, b, b, c, d);
    }
    const cos = new Float32Array(C), sin = new Float32Array(C);
    for (let j = 0; j < C; j++) { const th = (j / C) * Math.PI * 2; cos[j] = Math.cos(th); sin[j] = Math.sin(th); }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    const mesh = new T.Mesh(geo, mat);
    return { R, C, pos, cos, sin, geo, mesh };
  }

  // ===== Production GLB garments (glass material + liquid-glass melt shader) =====
  makeGlass() {
    const m = this.glassMat.clone();
    m.transparent = true;
    return m;
  }

  loadModels() {
    const T = this.T;
    const Loader = window.__GLTFLoader;
    if (!Loader) return;
    const loader = new Loader();
    const setup = (gltf, targetH, yOff) => {
      const obj = gltf.scene;
      const mat = this.makeGlass();
      obj.traverse((n) => { if (n.isMesh) { n.material = mat; if (n.geometry && !n.geometry.attributes.normal) n.geometry.computeVertexNormals(); } });
      const box = new T.Box3().setFromObject(obj);
      const size = box.getSize(new T.Vector3());
      const center = box.getCenter(new T.Vector3());
      obj.position.x -= center.x; obj.position.y -= center.y; obj.position.z -= center.z;
      const s = targetH / size.y;
      const holder = new T.Group();
      holder.add(obj);
      holder.scale.setScalar(s);
      holder.position.y = yOff || 0;
      holder.visible = false;
      holder.userData = { mat, baseScale: s, baseY: yOff || 0, sample: null };
      this.garGroup.add(holder);
      return holder;
    };
    this._loader = loader;
    this._setupModel = setup;
    // Pajama (Scene 1) loads immediately; the dress GLB is lazy-loaded just before Scene 2 needs it.
    this.loadGarment(loader, 'pajama', (g) => { this.pajamaModel = setup(g, 2.9, -0.3); this.pajamaModel.userData.sample = this.sampleModel(this.pajamaModel, this.tN); }, undefined, (e) => console.error('pajama glb', e));
  }

  // Desktop and mobile load the SAME optimized 48k-tri '-balanced' GLBs (QEM-decimated from
  // the 120k originals; silhouette/folds/glass verified identical in side-by-side QA).
  // Single code path — the only cross-device differences left are viewport-driven.
  // Falls back to the original file if a balanced asset is ever missing.
  loadGarment(loader, name, cb) {
    const url = (this._modelUrls && this._modelUrls[name]) || ('assets/' + name + '-balanced.glb');
    loader.load(url, cb, undefined, (e) => console.error(name + ' glb', e));
  }

  // Dress GLB is fetched only when the journey approaches Scene 2 (lazy load).
  ensureDress() {
    this._dressReq = true;
    const loader = this._loader, setup = this._setupModel;
    if (!loader || !setup) return;
    this.loadGarment(loader, 'dress', (g) => { this.dressModel = setup(g, 2.7, 0.2); this.dressModel.userData.sample = this.sampleModel(this.dressModel, this.tN); }, undefined, (e) => console.error('dress glb', e));
  }

  // Set uniform opacity across an object's meshes; hide entirely below threshold.
  setOpacity(model, op) {
    if (!model) return;
    const vis = op > 0.01;
    if (model.visible !== vis) model.visible = vis;
    if (!vis) return;
    model.traverse((n) => { if (n.isMesh && n.material) n.material.opacity = op; });
  }

  // Sample N surface points of an object into garGroup-LOCAL space (rotation removed via
  // worldToLocal), so the transition particles line up with where the object renders.
  sampleModel(obj, n) {
    const T = this.T;
    if (!n || !obj) return null;
    const meshes = [];
    obj.traverse((o) => { if (o.isMesh && o.geometry && o.geometry.attributes.position) meshes.push(o); });
    if (!meshes.length) return null;
    this.garGroup.updateWorldMatrix(true, false);
    obj.updateWorldMatrix(true, true);
    const out = new Float32Array(n * 3);
    const v = new T.Vector3();
    for (let i = 0; i < n; i++) {
      const mesh = meshes[(Math.random() * meshes.length) | 0];
      const pa = mesh.geometry.attributes.position;
      const idx = (Math.random() * pa.count) | 0;
      v.fromBufferAttribute(pa, idx);
      mesh.localToWorld(v);
      this.garGroup.worldToLocal(v);
      out[i * 3] = v.x; out[i * 3 + 1] = v.y; out[i * 3 + 2] = v.z;
    }
    return out;
  }

  buildTParticles() {
    const T = this.T;
    const n = this.tN = this.reduce ? 1000 : (this.isMobile ? 1200 : 3200);
    const pos = new Float32Array(n * 3);
    const siz = new Float32Array(n);
    const dir = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      let x = Math.random() * 2 - 1, y = Math.random() * 2 - 1, z = Math.random() * 2 - 1;
      const l = Math.hypot(x, y, z) || 1;
      dir[i * 3] = x / l; dir[i * 3 + 1] = y / l; dir[i * 3 + 2] = z / l;
      siz[i] = 0.6 + Math.random() * 1.7;
    }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(pos, 3));
    geo.setAttribute('aSize', new T.BufferAttribute(siz, 1));
    const mat = new T.ShaderMaterial({
      uniforms: { uOpacity: { value: 0 }, uPix: { value: 1 }, uTint: { value: new T.Color(0.82, 0.68, 1.0) }, uTime: { value: 0 } },
      transparent: true, depthWrite: false, blending: T.AdditiveBlending,
      vertexShader: 'attribute float aSize; uniform float uPix; uniform float uTime; varying float vTw; void main(){ vTw=0.6+0.4*sin(uTime*2.0+aSize*10.0); vec4 mv=modelViewMatrix*vec4(position,1.0); gl_PointSize=aSize*uPix*(150.0/-mv.z); gl_Position=projectionMatrix*mv; }',
      fragmentShader: 'uniform vec3 uTint; uniform float uOpacity; varying float vTw; void main(){ vec2 d=gl_PointCoord-0.5; float r=length(d); float a=smoothstep(0.5,0.0,r); gl_FragColor=vec4(uTint, a*uOpacity*vTw*0.5); }'
    });
    const points = new T.Points(geo, mat);
    points.frustumCulled = false; points.visible = false;
    this.garGroup.add(points);
    this.tparticles = { n, pos, dir, geo, mat, points };
  }

  // outM dissolves into particles, they bulge outward + drift, then collapse into inM.
  updateTParticles(outM, inM, q, amt, t) {
    const sys = this.tparticles;
    if (!sys) return;
    if (amt < 0.01 || !outM || !inM) { if (sys.points.visible) sys.points.visible = false; return; }
    if (!outM.userData.sample) outM.userData.sample = this.sampleModel(outM, sys.n);
    if (!inM.userData.sample) inM.userData.sample = this.sampleModel(inM, sys.n);
    const outS = outM.userData.sample, inS = inM.userData.sample;
    if (!outS || !inS) { sys.points.visible = false; return; }
    sys.points.visible = true;
    const n = sys.n, pos = sys.pos, dir = sys.dir;
    const mixv = q * q * (3 - 2 * q);
    const bulge = this.bell(q);
    for (let i = 0; i < n; i++) {
      const o = i * 3;
      const sp = bulge * (1.5 + (dir[o + 1] * 0.5 + 0.5) * 2.4);
      pos[o] = outS[o] + (inS[o] - outS[o]) * mixv + dir[o] * sp + Math.sin(t * 1.4 + i) * 0.02 * bulge;
      pos[o + 1] = outS[o + 1] + (inS[o + 1] - outS[o + 1]) * mixv + dir[o + 1] * sp + Math.cos(t * 1.2 + i) * 0.02 * bulge;
      pos[o + 2] = outS[o + 2] + (inS[o + 2] - outS[o + 2]) * mixv + dir[o + 2] * sp;
    }
    sys.geo.attributes.position.needsUpdate = true;
    sys.mat.uniforms.uTime.value = t;
    sys.mat.uniforms.uOpacity.value = amt * 0.5;
    sys.mat.uniforms.uPix.value = this.renderer.getPixelRatio();
  }

  // Final-scene abstract crystal sculpture: transparent faceted glass, asymmetric cluster.
  buildCrystal() {
    const T = this.T;
    const g = new T.Group();
    const glass = () => { const m = this.glassMat.clone(); m.transparent = true; m.flatShading = true; m.roughness = 0.02; m.iridescence = 0.85; m.needsUpdate = true; return m; };
    const mk = (geo, sc, px, py, pz, rx, ry, rz) => {
      const m = new T.Mesh(geo, glass());
      m.scale.set(sc[0], sc[1], sc[2]); m.position.set(px, py, pz); m.rotation.set(rx, ry, rz);
      m.frustumCulled = false; g.add(m); return m;
    };
    mk(new T.OctahedronGeometry(0.85, 0), [0.75, 2.0, 0.7], 0.05, 0.15, 0, 0.22, 0.5, 0.12);
    mk(new T.IcosahedronGeometry(0.6, 0), [1.0, 1.05, 0.85], 0.52, 0.62, -0.18, 0.5, 0.3, 0.85);
    mk(new T.TetrahedronGeometry(0.72, 0), [1.05, 1.5, 1.0], -0.5, -0.62, 0.28, 1.1, 0.25, 0.5);
    mk(new T.OctahedronGeometry(0.4, 0), [0.9, 0.9, 0.9], -0.35, 0.78, 0.35, 0.3, 0.8, 0.2);
    const glowTex = new T.CanvasTexture(this.softSprite('rgba(170,120,255,0.9)'));
    const glow = new T.Sprite(new T.SpriteMaterial({ map: glowTex, transparent: true, blending: T.AdditiveBlending, depthWrite: false, opacity: 0 }));
    glow.scale.set(5.2, 5.2, 1); glow.position.set(0, 0.15, -0.5);
    g.add(glow); this.crystalGlow = glow;
    g.visible = false;
    this.crystal = g;
    this.garGroup.add(g);
    this.crystal.userData = { sample: this.sampleModel(g, this.tN), baseScale: 1, baseY: 0, glow: glow };
  }

  // Small glowing glass core the garments shrink into and expand out of during transitions.
  buildCore() {
    const T = this.T;
    const g = new T.Group();
    const mat = this.glassMat.clone();
    mat.transparent = true; mat.flatShading = true; mat.roughness = 0.02; mat.iridescence = 0.9; mat.thickness = 1.2; mat.needsUpdate = true;
    const mesh = new T.Mesh(new T.IcosahedronGeometry(0.34, 0), mat);
    mesh.frustumCulled = false; g.add(mesh);
    const glowTex = new T.CanvasTexture(this.softSprite('rgba(185,145,255,0.95)'));
    const glow = new T.Sprite(new T.SpriteMaterial({ map: glowTex, transparent: true, blending: T.AdditiveBlending, depthWrite: false, opacity: 0 }));
    glow.scale.set(2.4, 2.4, 1); g.add(glow);
    g.visible = false;
    this.garGroup.add(g);
    this.core = { group: g, mesh, mat, glow };
  }

  // Opacity + uniform scale (baseScale*fac) for a loaded model holder; hides below threshold.
  applyModel(model, op, fac) {
    if (!model) return;
    const vis = op > 0.01;
    if (model.visible !== vis) model.visible = vis;
    if (!vis) return;
    model.scale.setScalar((model.userData.baseScale || 1) * fac);
    model.traverse((n) => { if (n.isMesh && n.material) n.material.opacity = op; });
    if (model.userData.glow) model.userData.glow.material.opacity = op * 0.5;
  }

  showCore(op, y, t) {
    const c = this.core; if (!c) return;
    const vis = op > 0.01;
    if (c.group.visible !== vis) c.group.visible = vis;
    if (!vis) return;
    c.group.position.y = y;
    c.group.scale.setScalar(1 + Math.sin(t * 1.1) * 0.05);
    c.mesh.rotation.set(t * 0.35, t * 0.62, 0);
    c.mat.opacity = op;
    c.glow.material.opacity = op * 0.6;
  }

  hideCore() { if (this.core && this.core.group.visible) this.core.group.visible = false; }

  // Per-scene showroom rotation: each garment turns slowly from its BACK (π) to FRONT (0),
  // then winds back to π through the melt so the next garment also begins from its back.
  // Motion is eased (smootherstep) so velocity is zero at every hand-off — no sudden movement.
  sceneRotation(p) {
    const sm = (x) => { x = Math.max(0, Math.min(1, x)); return x * x * x * (x * (x * 6 - 15) + 10); };
    const PI = Math.PI;
    if (p < 0.26) return PI * (1 - sm(p / 0.26));            // pajama: back -> front (scene 1)
    if (p < 0.44) return PI * sm((p - 0.26) / 0.18);          // wind front -> back through the transition
    if (p < 0.64) return PI * (1 - sm((p - 0.44) / 0.20));    // dress: back -> front (scene 2)
    return -0.6 * PI * sm((p - 0.64) / 0.36);                 // Others: slow showroom turn (scene 3)
  }

  buildGarments() {
    const T = this.T;
    this.body = this.makeTube(this.reduce ? 34 : 48, this.reduce ? 60 : 88, this.glassMat);
    const grp = new T.Group(); grp.scale.setScalar(0.92);
    grp.add(this.body.mesh);
    // sleeves
    this.sleeveL = this.makeTube(14, 20, this.glassMat);
    this.sleeveR = this.makeTube(14, 20, this.glassMat);
    grp.add(this.sleeveL.mesh); grp.add(this.sleeveR.mesh);
    // legs
    this.legL = this.makeTube(20, 22, this.glassMat);
    this.legR = this.makeTube(20, 22, this.glassMat);
    grp.add(this.legL.mesh); grp.add(this.legR.mesh);
    // wide folded cuffs (bands at each wrist)
    const cuffGeo = new T.CylinderGeometry(0.17, 0.17, 0.17, 22, 1, true);
    this.cuffL = new T.Mesh(cuffGeo, this.glassMat); this.cuffR = new T.Mesh(cuffGeo, this.glassMat);
    grp.add(this.cuffL); grp.add(this.cuffR);

    // ===== Pajama detail group (notched collar with folded lapels, wide placket, square pocket) =====
    this.pajamaDetail = new T.Group();
    const solid = this.glassMat.clone(); solid.transmission = 0.82; solid.thickness = 1.1; solid.emissiveIntensity = 0.6;
    // folded lapels: two long panels lying on the chest, angled along a deep V
    const lapelShape = new T.PlaneGeometry(0.24, 0.52);
    this.collarLp = new T.Mesh(lapelShape, solid); this.collarRp = new T.Mesh(lapelShape, solid);
    this.collarLp.position.set(-0.18, 0.92, 0.16); this.collarLp.rotation.set(0.04, 0.3, -0.42);
    this.collarRp.position.set(0.18, 0.92, 0.16); this.collarRp.rotation.set(0.04, -0.3, 0.42);
    this.pajamaDetail.add(this.collarLp); this.pajamaDetail.add(this.collarRp);
    // back collar stand behind the neck
    const collarBack = new T.Mesh(new T.BoxGeometry(0.36, 0.12, 0.1), solid);
    collarBack.position.set(0, 1.2, -0.03);
    this.pajamaDetail.add(collarBack);
    // wide front placket — from the bottom of the V to the hem
    const plackGeo = new T.BoxGeometry(0.13, 0.88, 0.05);
    this.placket = new T.Mesh(plackGeo, solid);
    this.placket.position.set(0, 0.12, 0.18);
    this.pajamaDetail.add(this.placket);
    // large square chest pocket, left side
    const pkGeo = new T.BoxGeometry(0.22, 0.22, 0.03);
    this.pocket = new T.Mesh(pkGeo, solid);
    this.pocket.position.set(-0.28, 0.55, 0.17); this.pocket.rotation.y = -0.15;
    this.pajamaDetail.add(this.pocket);
    grp.add(this.pajamaDetail);

    // buttons (7, glowing, down the placket)
    const bmat = this.glassMat.clone(); bmat.emissiveIntensity = 1.1; bmat.transmission = 0.5;
    this.buttonsGroup = new T.Group();
    const bgeo = new T.SphereGeometry(0.045, 14, 12);
    this.buttons = [];
    for (let i = 0; i < 7; i++) { const m = new T.Mesh(bgeo, bmat); this.buttons.push(m); this.buttonsGroup.add(m); }
    grp.add(this.buttonsGroup);

    // high-waist pants waistband (flat band) + drawstring bow — overlapped by the shirt hem
    this.waistGroup = new T.Group();
    const wbGeo = new T.CylinderGeometry(0.47, 0.47, 0.13, 30, 1, true);
    this.waistband = new T.Mesh(wbGeo, solid);
    this.waistband.scale.set(1, 1, 0.36);
    this.waistGroup.add(this.waistband);
    // drawstring: two short tails + a center knot + two loops
    const dsMat = this.glassMat.clone(); dsMat.emissiveIntensity = 1.0; dsMat.transmission = 0.5;
    const tailGeo = new T.BoxGeometry(0.03, 0.22, 0.03);
    const dsTailL = new T.Mesh(tailGeo, dsMat); dsTailL.position.set(-0.05, -0.13, 0.2); dsTailL.rotation.z = 0.2;
    const dsTailR = new T.Mesh(tailGeo, dsMat); dsTailR.position.set(0.05, -0.13, 0.2); dsTailR.rotation.z = -0.2;
    const knot = new T.Mesh(new T.SphereGeometry(0.035, 12, 10), dsMat); knot.position.set(0, 0, 0.22);
    const dsLoopGeo = new T.SphereGeometry(0.055, 12, 10);
    const loopL = new T.Mesh(dsLoopGeo, dsMat); loopL.scale.set(1.6, 0.85, 0.5); loopL.position.set(-0.08, 0.02, 0.21);
    const loopR = new T.Mesh(dsLoopGeo, dsMat); loopR.scale.set(1.6, 0.85, 0.5); loopR.position.set(0.08, 0.02, 0.21);
    this.waistGroup.add(dsTailL); this.waistGroup.add(dsTailR); this.waistGroup.add(knot); this.waistGroup.add(loopL); this.waistGroup.add(loopR);
    grp.add(this.waistGroup);

    // ===== Nightdress detail group (thin straps + vertical column of bows) =====
    this.nightDetail = new T.Group();
    const strapGeo = new T.BoxGeometry(0.05, 0.34, 0.05);
    this.strapL = new T.Mesh(strapGeo, solid); this.strapR = new T.Mesh(strapGeo, solid);
    this.strapL.position.set(-0.19, 1.0, 0.16); this.strapL.rotation.z = 0.12;
    this.strapR.position.set(0.19, 1.0, 0.16); this.strapR.rotation.z = -0.12;
    this.nightDetail.add(this.strapL); this.nightDetail.add(this.strapR);
    // bows: a pinched center knot + two wide loops (small flattened spheres)
    this.bows = [];
    const bowMat = this.glassMat.clone(); bowMat.emissiveIntensity = 1.0; bowMat.transmission = 0.55;
    const knotGeo = new T.SphereGeometry(0.03, 10, 8);
    const loopGeo = new T.SphereGeometry(0.05, 10, 8);
    for (let i = 0; i < 7; i++) {
      const b = new T.Group();
      const knot = new T.Mesh(knotGeo, bowMat);
      const lpL = new T.Mesh(loopGeo, bowMat); lpL.scale.set(1.5, 0.8, 0.5); lpL.position.x = -0.07;
      const lpR = new T.Mesh(loopGeo, bowMat); lpR.scale.set(1.5, 0.8, 0.5); lpR.position.x = 0.07;
      b.add(knot); b.add(lpL); b.add(lpR);
      this.bows.push(b); this.nightDetail.add(b);
    }
    grp.add(this.nightDetail);

    this.garGroup = grp; this.scene.add(grp);
  }

  updateTube(tube, profile, yb, yt, amp, freq) {
    const { R, C, pos, cos, sin, geo } = tube;
    const t = this.t;
    for (let i = 0; i < R; i++) {
      const tt = i / (R - 1);
      const rad = this.profileR(profile, tt);
      const y = yb + (yt - yb) * tt;
      for (let j = 0; j < C; j++) {
        const th = (j / C) * Math.PI * 2;
        const w = Math.sin(th * (freq || 6) + t * 1.1 + tt * 5) * 0.5 + Math.sin(th * 10 - t * 0.7 + tt * 8) * 0.28;
        const rr = rad + amp * w;
        const o = (i * C + j) * 3;
        pos[o] = cos[j] * rr; pos[o + 1] = y; pos[o + 2] = sin[j] * rr;
      }
    }
    geo.attributes.position.needsUpdate = true;
    geo.computeVertexNormals();
    geo.computeBoundingSphere();
  }

  buildRibbons() {
    const T = this.T;
    this.ribbons = []; const grp = new T.Group();
    for (let k = 0; k < 4; k++) {
      const RN = 64; const pos = new Float32Array(RN * 2 * 3);
      const geo = new T.BufferGeometry(); geo.setAttribute('position', new T.BufferAttribute(pos, 3));
      const idx = []; for (let i = 0; i < RN - 1; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      geo.setIndex(idx);
      const mat = new T.MeshPhysicalMaterial({ color: new T.Color(0xcbb8ff), metalness: 0, roughness: 0.18, transmission: 0.85, thickness: 0.6, ior: 1.4, clearcoat: 1, side: T.DoubleSide, transparent: true, opacity: 0, envMapIntensity: 1.6, iridescence: 0.5 });
      const mesh = new T.Mesh(geo, mat); grp.add(mesh);
      this.ribbons.push({ mesh, geo, pos, RN, mat, phase: k * 1.7, seed: k });
    }
    this.ribbonGroup = grp; this.scene.add(grp);
  }

  buildStars() {
    const T = this.T;
    const N = this.reduce ? 480 : (this.isMobile ? 460 : 1860);
    this.starN = N; this.starRange = 220; this.starZ0 = -200;
    const pos = new Float32Array(N * 3), siz = new Float32Array(N), pha = new Float32Array(N), tnt = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 180;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 130;
      pos[i * 3 + 2] = this.starZ0 + Math.random() * this.starRange;
      siz[i] = 0.5 + Math.random() * 2.6; pha[i] = 0.5 + Math.random() * 3; tnt[i] = Math.random() < 0.32 ? 1 : 0;
    }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(pos, 3));
    geo.setAttribute('aSize', new T.BufferAttribute(siz, 1));
    geo.setAttribute('aPhase', new T.BufferAttribute(pha, 1));
    geo.setAttribute('aTint', new T.BufferAttribute(tnt, 1));
    this.starPos = pos; this.starGeo = geo;
    const mat = new T.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uPix: { value: 1 }, uTint: { value: new T.Color(0.75, 0.6, 1.0) } },
      transparent: true, depthWrite: false, blending: T.AdditiveBlending,
      vertexShader: 'attribute float aSize; attribute float aPhase; attribute float aTint; uniform float uTime; uniform float uPix; varying float vT; varying float vTint; void main(){ vTint=aTint; vT=0.55+0.45*sin(uTime*aPhase+aPhase*3.0); vec4 mv=modelViewMatrix*vec4(position,1.0); gl_PointSize=aSize*uPix*(150.0/-mv.z); gl_Position=projectionMatrix*mv; }',
      fragmentShader: 'uniform vec3 uTint; varying float vT; varying float vTint; void main(){ vec2 d=gl_PointCoord-0.5; float r=length(d); float a=smoothstep(0.5,0.0,r); a*=vT; vec3 col=mix(vec3(0.93,0.95,1.0), uTint, vTint*0.9); gl_FragColor=vec4(col, a); }'
    });
    this.starMat = mat; this.stars = new T.Points(geo, mat); this.stars.frustumCulled = false; this.scene.add(this.stars);
  }

  buildParticles() {
    const T = this.T;
    const N = this.reduce ? 60 : (this.isMobile ? 55 : 210);
    this.partN = N;
    const pos = new Float32Array(N * 3), siz = new Float32Array(N), pha = new Float32Array(N);
    this.partBase = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const x = (Math.random() - 0.5) * 40, y = (Math.random() - 0.5) * 26, z = -6 - Math.random() * 30;
      pos[i * 3] = x; pos[i * 3 + 1] = y; pos[i * 3 + 2] = z;
      this.partBase[i * 3] = x; this.partBase[i * 3 + 1] = y; this.partBase[i * 3 + 2] = z;
      siz[i] = 1.2 + Math.random() * 3.4; pha[i] = Math.random() * 6.28;
    }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(pos, 3));
    geo.setAttribute('aSize', new T.BufferAttribute(siz, 1));
    geo.setAttribute('aPhase', new T.BufferAttribute(pha, 1));
    this.partPos = pos; this.partGeo = geo;
    const mat = new T.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uPix: { value: 1 }, uTint: { value: new T.Color(0.8, 0.66, 1.0) } },
      transparent: true, depthWrite: false, blending: T.AdditiveBlending,
      vertexShader: 'attribute float aSize; attribute float aPhase; uniform float uTime; uniform float uPix; varying float vA; void main(){ vA=0.35+0.35*sin(uTime*0.6+aPhase); vec4 mv=modelViewMatrix*vec4(position,1.0); gl_PointSize=aSize*uPix*(140.0/-mv.z); gl_Position=projectionMatrix*mv; }',
      fragmentShader: 'uniform vec3 uTint; varying float vA; void main(){ vec2 d=gl_PointCoord-0.5; float r=length(d); float a=smoothstep(0.5,0.0,r); gl_FragColor=vec4(uTint, a*vA); }'
    });
    this.partMat = mat; this.particles = new T.Points(geo, mat); this.particles.frustumCulled = false; this.scene.add(this.particles);
  }

  buildNebula() {
    const T = this.T;
    const grp = new T.Group();
    const cols = ['rgba(150,90,255,0.9)', 'rgba(210,120,200,0.85)', 'rgba(90,110,255,0.85)', 'rgba(120,70,200,0.8)', 'rgba(180,110,230,0.85)', 'rgba(70,90,220,0.8)', 'rgba(200,130,240,0.8)', 'rgba(110,80,210,0.8)'];
    this.nebula = [];
    for (let i = 0; i < 8; i++) {
      const tex = new T.CanvasTexture(this.softSprite(cols[i]));
      const mat = new T.SpriteMaterial({ map: tex, transparent: true, blending: T.AdditiveBlending, depthWrite: false, opacity: 0.26 });
      const sp = new T.Sprite(mat); const sc = 34 + Math.random() * 52; sp.scale.set(sc, sc, 1);
      sp.position.set((Math.random() - 0.5) * 110, (Math.random() - 0.5) * 70, -26 - Math.random() * 100);
      sp.userData.spin = (Math.random() - 0.5) * 0.06;
      grp.add(sp); this.nebula.push(sp);
    }
    this.nebulaGroup = grp; this.scene.add(grp);
  }

  resize() {
    if (!this.ready) return;
    const stage = this.stageRef.current; if (!stage) return;
    const w = stage.clientWidth, h = stage.clientHeight;
    this.vw = w; this.vh = h; this.mobile = w < 820;
    const sc = this.rootRef.current;
    const _track = this.heroTrackRef && this.heroTrackRef.current;
    this.maxScroll = _track ? Math.max(1, _track.offsetHeight - sc.clientHeight) : (sc ? sc.scrollHeight - sc.clientHeight : 1);
    this._trackH = _track ? _track.offsetHeight : 0;
    this._dprCap = Math.min(window.devicePixelRatio || 1, this.isMobile ? 1.5 : 2);
    const dpr = Math.min(this._dprCap, this._dprAdaptive || this._dprCap);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.cam.aspect = w / h; this.cam.updateProjectionMatrix();
    if (this.starMat) this.starMat.uniforms.uPix.value = dpr;
    if (this.partMat) this.partMat.uniforms.uPix.value = dpr;
    this.gx = this.mobile ? 0 : 0.7;
    // Single authoritative garment scale: original build-time 0.92 on desktop;
    // mobile compensates for the desktop polish (bigger GLB target heights + closer dolly)
    // that was applied unconditionally and oversized the hero garments on phones.
    if (this.garGroup) this.garGroup.scale.setScalar(this.mobile ? 0.92 * 0.78 : 0.92);
    // Mobile: lift the garment so it never covers the bottom-anchored title/copy/CTA.
    this.gyOff = this.mobile ? 0.3 : 0;
    this.applyLayout();
  }

  applyLayout() {
    const m = this.mobile;
    [this.s1Ref, this.s2Ref, this.s3Ref].forEach(r => {
      const el = r.current; if (!el) return;
      el.style.left = m ? '7vw' : '8vw';
      el.style.right = m ? '7vw' : 'auto';
      el.style.top = m ? 'auto' : '50%';
      el.style.bottom = m ? '14vh' : 'auto';
      el.style.transform = m ? 'none' : 'translateY(-50%)';
      el.style.maxWidth = m ? 'none' : '46vw';
      const inner = el.firstElementChild;
      if (inner) inner.style.alignItems = 'flex-start';
    });
  }

  // ---- premium product spinner: user drag/swipe rotates ONLY the garment group (Y axis) ----
  // Inertia: heavy exponential damping (~88% speed lost per second), capped max speed,
  // full stop below a threshold. Resets to default rotation when the hero scene changes.
  updateSpin(dt, p) {
    const scene = p < 0.37 ? 0 : (p < 0.71 ? 1 : 2);
    if (scene !== this._spinScene) { this._spinScene = scene; this.spinY = 0; this.spinVel = 0; }
    if (!this._spinDragging && this.spinVel) {
      this.spinVel *= Math.pow(0.12, dt);
      if (Math.abs(this.spinVel) < 0.02) this.spinVel = 0;
      this.spinY += this.spinVel * dt;
    }
    return this.spinY || 0;
  }
  bindSpinner() {
    const cv = this.canvasRef.current; if (!cv) return;
    this.spinY = 0; this.spinVel = 0; this._spinScene = 0;
    if (!this.isMobile) cv.style.cursor = 'grab';
    // Desktop: click-drag on the canvas. Mobile input rides the existing snap touch handlers.
    const onDown = (e) => {
      if (this.isMobile || this._renderIdle || e.button !== 0) return;
      this._spinDragging = true; this._sx = e.clientX; this._st = performance.now(); this._sv = 0; this.spinVel = 0;
      cv.style.cursor = 'grabbing'; e.preventDefault();
    };
    const onMove = (e) => {
      if (!this._spinDragging || this.isMobile) return;
      const now = performance.now(); const dRot = (e.clientX - this._sx) * 0.008;
      this.spinY += dRot;
      this._sv = 0.75 * this._sv + 0.25 * (dRot * 1000 / Math.max(1, now - this._st));
      this._sx = e.clientX; this._st = now;
    };
    const onUp = () => {
      if (!this._spinDragging) return;
      this._spinDragging = false; cv.style.cursor = 'grab';
      this.spinVel = Math.max(-4, Math.min(4, this._sv || 0));
    };
    cv.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    this._spinCleanup = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }

  loop() {
    if (this.disposed) return;
    // Hero off-screen (scrolled into the shop): stop the continuous rAF/render cycle entirely —
    // zero GPU and near-zero CPU. A cheap 8Hz scroll check resumes the loop the moment the
    // sticky stage can become visible again; the last rendered frame stays on the canvas so
    // the hand-back is invisible.
    const scElI = this.rootRef.current;
    if (this.ready && this._trackH && scElI && scElI.scrollTop > this._trackH + 4) {
      this._renderIdle = true;
      this.clock.getDelta(); // keep the clock current so resuming has no time jump
      clearTimeout(this._idleT);
      this._idleT = setTimeout(this.loop, 125);
      return;
    }
    const dt = Math.min(this.clock.getDelta(), 0.05);
    try { this.frame(dt); } catch (e) { if (!this._warned) { console.error('frame error', e); this._warned = true; } }
    // Adaptive pixel ratio: steps down only when FPS is actually low, back up when healthy.
    this._fpsT = (this._fpsT || 0) + dt; this._fpsN = (this._fpsN || 0) + 1;
    if (this._fpsT >= 1.5) { const fps = this._fpsN / this._fpsT; this._fpsT = 0; this._fpsN = 0; this.adaptDpr(fps); }
    this.raf = requestAnimationFrame(this.loop);
  }

  adaptDpr(fps) {
    if (!this.renderer || this._renderIdle) return;
    const cap = this._dprCap || 2;
    const cur = this._dprAdaptive || cap;
    let next = cur;
    if (fps < 45 && cur > 1) next = Math.max(1, cur - 0.25);
    else if (fps > 57 && cur < cap) next = Math.min(cap, cur + 0.25);
    if (next !== cur) {
      this._dprAdaptive = next;
      this.renderer.setPixelRatio(next);
      if (this.starMat) this.starMat.uniforms.uPix.value = next;
      if (this.partMat) this.partMat.uniforms.uPix.value = next;
    }
  }

  frame(dt) {
    if (!this.ready) return;
    this.renderP += (this.targetP - this.renderP) * 0.14;
    // Below the hero the sticky 3D stage is off-screen: skip every render pass and
    // all scene work (visibility culling of the whole scene) until scrolled back.
    const scEl = this.rootRef.current;
    this._renderIdle = !!(this._trackH && scEl && scEl.scrollTop > this._trackH + 4);
    if (this._renderIdle) { this.lastP = this.renderP; return; }
    const p = this.renderP;
    // Lazy-load the Scene 2 garment shortly before it can appear.
    if (!this._dressReq && p > 0.12) this.ensureDress();
    const vel = Math.abs(p - this.lastP); this.lastP = p;
    this.t += dt; const t = this.t;
    const S = (a, b) => this.S(a, b, p);
    // Layout depends only on this.mobile: applied by resize(); re-run per-frame just until
    // all three copy refs have mounted once (first frames), then never again.
    if (!this._layoutOnce) { this._layoutOnce = !!(this.s1Ref.current && this.s2Ref.current && this.s3Ref.current); this.applyLayout(); }

    let wa = 1 - S(0.28, 0.46);
    const wc = S(0.62, 0.8);
    let wb = this.clamp(1 - wa - wc, 0, 1);
    const melt = Math.max(this.bell(this.clamp((p - 0.28) / 0.18, 0, 1)), this.bell(this.clamp((p - 0.62) / 0.18, 0, 1)));

    this.glassMat.roughness = 0.035 - 0.02 * melt;
    this.glassMat.thickness = 0.7 + melt * 1.1;
    this.glassMat.iridescence = 0.7 + 0.25 * melt;
    // The procedural garment below was replaced by the production GLB models and is set
    // invisible unconditionally further down — skip its per-frame rebuild (pure CPU cost,
    // zero visual output). Flip this._proceduralGarment to true to re-enable it.
    if (this._proceduralGarment) {
    // ---- body ----
    const R = this.body.R, C = this.body.C, pos = this.body.pos;
    const yb = this.byA[0] * wa + this.byB[0] * wb + this.byC[0] * wc;
    const yt = this.byA[1] * wa + this.byB[1] * wb + this.byC[1] * wc;
    const wrink = 0.016 + melt * 0.15;
    const sag = melt * 0.45;
    const flat = this.flatA * wa + this.flatB * wb + this.flatC * wc;
    const ribAmp = 0.02 * wb * (1 - melt);      // vertical ribbing, nightdress only, fades during melt
    const waveAmt = 0.05 * wb * (1 - melt);     // wavy lettuce edges at neck + hem (nightdress)
    for (let i = 0; i < R; i++) {
      const tt = i / (R - 1);
      let rad = this.profileR(this.pA, tt) * wa + this.profileR(this.pB, tt) * wb + this.profileR(this.pC, tt) * wc;
      let y = yb + (yt - yb) * tt;
      y -= sag * Math.sin(tt * Math.PI) * (0.4 + 0.3 * Math.sin(t * 1.3));
      // lettuce edge weight: strongest at the very hem and neck
      const edge = Math.max(tt < 0.1 ? (0.1 - tt) / 0.1 : 0, tt > 0.9 ? (tt - 0.9) / 0.1 : 0);
      // rounded shirt hem (pajama): dips at center front/back, sweeps up toward the side seams
      const hemW = tt < 0.14 ? (0.14 - tt) / 0.14 : 0;
      const base = i * C;
      for (let j = 0; j < C; j++) {
        const th = (j / C) * Math.PI * 2;
        const w1 = Math.sin(th * 6 + t * 1.1 + tt * 5) * 0.5;
        const w2 = Math.sin(th * 11 - t * 0.7 + tt * 9) * 0.28;
        const w3 = Math.sin(tt * 22 + t * 1.6 + th * 2) * 0.3;
        const ribs = ribAmp * Math.cos(th * 26);
        const rr = rad + wrink * (w1 + w2 + w3) + ribs + 0.01 * Math.sin(t * 0.9 + tt * 4);
        const sj = this.body.sin[j];
        let yy = y + edge * waveAmt * Math.sin(th * 9 + t * 0.6);
        yy -= wa * hemW * 0.11 * sj * sj;
        const o = (base + j) * 3;
        pos[o] = this.body.cos[j] * rr; pos[o + 1] = yy; pos[o + 2] = sj * rr * flat;
      }
    }
    this.body.geo.attributes.position.needsUpdate = true;
    this.body.geo.computeVertexNormals();
    this.body.geo.computeBoundingSphere();

    // ---- sleeves / legs / collar / buttons (scene 1 pajama parts) ----
    const partAmp = 0.01 + melt * 0.12;
    const sleeveAmt = wa;
    const legAmt = wa;
    const collarAmt = wa + wb * 0.3;
    const buttonAmt = this.clamp(wa - S(0.2, 0.3), 0, 1);

    // Pajama sleeves: loose, long, flared cuff; Nightdress sleeves: fitted long. Blend width by scene.
    // Pajama sleeves: loose with gentle volume (elbow fullness), long. Nightdress sleeves: fitted. Blend by scene.
    const sleeveTop = 0.17 * wa + 0.1 * wb, sleeveMid = 0.165 * wa + 0.085 * wb, sleeveCuff = 0.15 * wa + 0.07 * wb;
    const sLen = 1.55 * wa + 1.3 * wb;
    const sleeveProf = [[0, sleeveCuff], [0.1, sleeveCuff * 1.26], [0.22, sleeveCuff * 0.92], [0.45, sleeveMid * 1.06], [0.72, sleeveMid], [1, sleeveTop]];
    this.updateTube(this.sleeveL, sleeveProf, -sLen, 0, partAmp, 5);
    this.updateTube(this.sleeveR, sleeveProf, -sLen, 0, partAmp, 5);
    // Wide relaxed straight legs: high waist, gentle fall, slight gathering just above the ankle hem
    const legProf = [[0, 0.05], [0.03, 0.17], [0.07, 0.155], [0.16, 0.23], [0.5, 0.235], [0.88, 0.245], [1, 0.17]];
    this.updateTube(this.legL, legProf, -1.72, 0, partAmp * 1.4, 4);
    this.updateTube(this.legR, legProf, -1.72, 0, partAmp * 1.4, 4);

    const shoulderY = 0.96, waistY = -0.14;
    // flatten factor: flat flat-lay sleeves in pajama, rounder & fitted in nightdress
    const sleeveAmt2 = this.clamp(wa + wb, 0, 1);
    const sleeveFlat = (0.48 * wa + 0.85 * wb) / Math.max(sleeveAmt2, 0.0001);
    const sl = this.sleeveL, sr = this.sleeveR;
    // relaxed (wide) shoulders + sleeves angled down-and-out like the reference flat-lay
    const outX = 0.55 * wa + 0.32 * wb, angle = 0.24 * wa + 0.16 * wb, drop = wb * 0.15;
    sl.mesh.visible = sleeveAmt2 > 0.02; sr.mesh.visible = sleeveAmt2 > 0.02;
    sl.mesh.scale.set(sleeveAmt2, sleeveAmt2, sleeveAmt2 * sleeveFlat); sr.mesh.scale.set(sleeveAmt2, sleeveAmt2, sleeveAmt2 * sleeveFlat);
    // left splays LEFT (rot -angle), right splays RIGHT (rot +angle) — outward like the flat-lay
    sl.mesh.position.set(-outX, shoulderY - 0.02 - drop, 0.05); sl.mesh.rotation.set(0.08, 0, -angle);
    sr.mesh.position.set(outX, shoulderY - 0.02 - drop, 0.05); sr.mesh.rotation.set(0.08, 0, angle);

    // folded cuffs — a wider band exactly at each rotated sleeve end
    const cf = [this.cuffL, this.cuffR];
    const Leff = sLen * sleeveAmt2;
    for (let s = 0; s < 2; s++) {
      const sm = s === 0 ? sl : sr, th = s === 0 ? -angle : angle;
      const endX = sm.mesh.position.x + Leff * Math.sin(th);
      const endY = sm.mesh.position.y - Leff * Math.cos(th);
      cf[s].visible = sleeveAmt2 > 0.02;
      cf[s].scale.set(sleeveAmt2 * 1.3, sleeveAmt2, sleeveAmt2 * sleeveFlat * 1.3);
      cf[s].position.set(endX, endY, 0.05); cf[s].rotation.z = th;
    }

    // legs (loose straight pants) only in scene 1 — clearly below the shirt, flattened, side by side
    const ll = this.legL, lr = this.legR, lIn = 1 - legAmt;
    const legFlat = 0.38;
    ll.mesh.visible = legAmt > 0.02; lr.mesh.visible = legAmt > 0.02;
    ll.mesh.scale.set(legAmt, legAmt, legAmt * legFlat); lr.mesh.scale.set(legAmt, legAmt, legAmt * legFlat);
    ll.mesh.position.set(-0.22, waistY - 0.06 + lIn * 0.5, 0); ll.mesh.rotation.set(0, 0, 0.015);
    lr.mesh.position.set(0.22, waistY - 0.06 + lIn * 0.5, 0); lr.mesh.rotation.set(0, 0, -0.015);

    // pants waistband + drawstring bow (scene 1) — sits at the waist, bow peeking in front of the shirt hem
    this.waistGroup.visible = wa > 0.02;
    this.waistGroup.scale.setScalar(wa);
    this.waistGroup.position.set(0, waistY + (1 - wa) * 0.4, 0);

    // pajama details (collar / placket / pocket) fade with scene 1
    this.pajamaDetail.visible = wa > 0.02;
    this.pajamaDetail.scale.setScalar(wa);
    this.buttonsGroup.visible = buttonAmt > 0.02;
    this.buttonsGroup.scale.setScalar(buttonAmt);
    for (let i = 0; i < this.buttons.length; i++) {
      const by = 0.52 - i * 0.13;
      this.buttons[i].position.set(0, by, 0.2 + 0.01 * Math.sin(t + i));
    }

    // nightdress details (straps + vertical bow column) fade with scene 2
    this.nightDetail.visible = wb > 0.02;
    this.nightDetail.scale.setScalar(wb);
    for (let i = 0; i < this.bows.length; i++) {
      const by = 0.82 - i * 0.2;
      this.bows[i].position.set(0, by, 0.34 + 0.015 * Math.sin(t + i));
      this.bows[i].rotation.z = 0.04 * Math.sin(t * 0.8 + i);
    }

    } // end this._proceduralGarment (dead per-frame rebuild skipped)

    this.garGroup.position.x = this.gx;
    this.garGroup.position.y = Math.sin(t * 0.25) * 0.06 + (this.gyOff || 0);
    this.garGroup.rotation.y = this.sceneRotation(p) + Math.sin(t * 0.2) * 0.02 + this.updateSpin(dt, p);
    this.garGroup.rotation.z = 0;
    this.garGroup.rotation.x = 0;

    // ===== Production GLB garments replace procedural pajama & dress display =====
    // hide every procedural garment piece; keep the low-poly body only for scene 3 (Others gown)
    this.pajamaDetail.visible = false; this.buttonsGroup.visible = false; this.waistGroup.visible = false;
    this.nightDetail.visible = false;
    this.sleeveL.mesh.visible = false; this.sleeveR.mesh.visible = false;
    this.legL.mesh.visible = false; this.legR.mesh.visible = false;
    this.cuffL.visible = false; this.cuffR.visible = false;
    this.body.mesh.visible = false;
    if (this.tparticles) this.tparticles.points.visible = false;
    // ===== Liquid-crystal transition: the garment softens, compresses inward, and shrinks into a
    // small glowing glass core; the core rotates slowly; then it expands into the next garment.
    // No particles, no flash — one continuous glass form changing shape (Apple-smooth). =====
    const ez = (x) => { x = this.clamp(x, 0, 1); return x * x * x * (x * (x * 6 - 15) + 10); };
    const lin = (a, b) => this.clamp((p - a) / (b - a), 0, 1);
    const present = (outM, inM, q) => {
      const yOut = (outM && outM.userData.baseY) || 0, yIn = (inM && inM.userData.baseY) || 0;
      this.applyModel(outM, 1 - ez(this.clamp((q - 0.16) / 0.32, 0, 1)), 1 - ez(this.clamp(q / 0.5, 0, 1)) * 0.94);
      this.applyModel(inM, ez(this.clamp((q - 0.52) / 0.32, 0, 1)), 0.06 + ez(this.clamp((q - 0.5) / 0.5, 0, 1)) * 0.94);
      const appear = ez(this.clamp((q - 0.24) / 0.22, 0, 1)), vanish = ez(this.clamp((q - 0.56) / 0.22, 0, 1));
      this.showCore(appear * (1 - vanish), yOut + (yIn - yOut) * ez(q), t);
    };
    let opP = 0, opD = 0, opX = 0, trans = false;
    if (p < 0.28) { opP = 1; }
    else if (p < 0.46) { present(this.pajamaModel, this.dressModel, lin(0.28, 0.46)); trans = true; }
    else if (p < 0.62) { opD = 1; }
    else if (p < 0.80) { present(this.dressModel, this.crystal, lin(0.62, 0.80)); trans = true; }
    else { opX = 1; }
    if (!trans) {
      this.applyModel(this.pajamaModel, opP, 1);
      this.applyModel(this.dressModel, opD, 1);
      this.applyModel(this.crystal, opX, 1);
      this.hideCore();
    }
    // gentle idle float + rotation for the crystal whenever it is on screen
    if (this.crystal && this.crystal.visible) {
      this.crystal.rotation.x = Math.sin(t * 0.18) * 0.22;
      this.crystal.rotation.z = Math.cos(t * 0.13) * 0.16;
      this.crystal.position.y = Math.sin(t * 0.32) * 0.06;
    } else if (this.crystal) {
      this.crystal.rotation.x = 0; this.crystal.rotation.z = 0; this.crystal.position.y = 0;
    }

    // ---- ribbons: disabled (replaced by the particle-dissolve transition) ----
    const ribAmt = 0;
    this.ribbonGroup.visible = false;
    if (false) {
      for (let k = 0; k < this.ribbons.length; k++) {
        const rb = this.ribbons[k]; rb.mat.opacity = ribAmt * 0.75; rb.mat.transmission = 0.7 * ribAmt;
        const RN = rb.RN, baseA = rb.phase + t * 0.4 + p * 3;
        for (let i = 0; i < RN; i++) {
          const u = i / (RN - 1);
          const ang = baseA + u * Math.PI * 4 + Math.sin(t * 0.6 + rb.seed) * 0.5;
          const rad = 1.4 + Math.sin(u * Math.PI) * 1.7 + Math.sin(t + rb.seed) * 0.2;
          const cx = Math.cos(ang) * rad + this.gx, cz = Math.sin(ang) * rad;
          const cy = (u - 0.5) * 3.6 + Math.sin(t * 0.8 + u * 6 + rb.seed) * 0.3;
          const wdt = 0.13, nx = Math.cos(ang + Math.PI / 2) * wdt, nz = Math.sin(ang + Math.PI / 2) * wdt;
          rb.pos[i * 6] = cx - nx; rb.pos[i * 6 + 1] = cy; rb.pos[i * 6 + 2] = cz - nz;
          rb.pos[i * 6 + 3] = cx + nx; rb.pos[i * 6 + 4] = cy; rb.pos[i * 6 + 5] = cz + nz;
        }
        rb.geo.attributes.position.needsUpdate = true; rb.geo.computeVertexNormals(); rb.geo.computeBoundingSphere();
      }
    }

    // ---- camera: hold in FRONT (the garment turns, not the camera); only subtle premium drift + dolly ----
    const az = (Math.sin(t * 0.09) * 0.14 + Math.sin(t * 0.13) * 0.05) * (this.isMobile ? 0.35 : 1);
    let el = 0.1 + 0.28 * S(0.42, 0.56) - 0.32 * S(0.72, 0.86) + Math.sin(t * 0.1) * 0.05;
    let r = 9.2;
    r -= 3.4 * S(0.05, 0.2);
    r += 1.1 * S(0.3, 0.45);
    r -= 1.2 * S(0.46, 0.6);
    r += 1.2 * S(0.62, 0.75);
    r -= 1.1 * S(0.78, 0.9);
    r += 3.6 * S(0.94, 1.0);
    r += Math.sin(t * 0.2) * 0.4;
    r = Math.max(4.5, r);
    const ce = Math.cos(el);
    const floatY = Math.sin(t * 0.15) * 0.18 + Math.sin(t * 0.23) * 0.08;
    this.cam.position.set(Math.sin(az) * ce * r + this.gx * 0.6, Math.sin(el) * r + 0.35 + floatY, Math.cos(az) * ce * r);
    const ty = -0.1 + 0.25 * S(0.42, 0.56);
    this.cam.up.set(this.isMobile ? 0 : Math.sin(t * 0.08) * 0.06, 1, 0);
    this.cam.lookAt(this.gx, ty, 0);
    const fov = (42 + 5 * melt - 3 * S(0.05, 0.2));
    if (Math.abs(fov - this.cam.fov) > 0.05) { this.cam.fov = fov; this.cam.updateProjectionMatrix(); }

    // ---- travel through stars ----
    const travel = (12 + vel * 1100) * dt * 6;
    const range = this.starRange, sp = this.starPos;
    for (let i = 0; i < this.starN; i++) { let z = sp[i * 3 + 2] + travel; if (z > 16) z -= range; sp[i * 3 + 2] = z; }
    this.starGeo.attributes.position.needsUpdate = true;
    this.starMat.uniforms.uTime.value = t;

    // ---- floating particles (parallax with camera + drift) ----
    const pb = this.partBase, pp = this.partPos;
    const drift = t * 0.6;
    for (let i = 0; i < this.partN; i++) {
      const o = i * 3;
      pp[o] = pb[o] + Math.sin(t * 0.5 + i) * 0.9 + Math.sin(t * 0.12 + i * 0.3) * 1.4 - Math.sin(az) * 2.2;
      pp[o + 1] = ((pb[o + 1] + drift + Math.cos(t * 0.34 + i * 1.3) * 0.7 - p * 5 + 13) % 26) - 13;
      pp[o + 2] = pb[o + 2] + Math.sin(t * 0.3 + i) * 0.7;
    }
    this.partGeo.attributes.position.needsUpdate = true;
    this.partMat.uniforms.uTime.value = t;

    // ---- color / volumetric light ----
    const col = this.mix3(this.c1, this.c2, this.c3, this.clamp(p, 0, 1));
    const cr = col[0] / 255, cg = col[1] / 255, cb = col[2] / 255;
    this.keyLight.color.setRGB(cr * 1.05, cg * 0.85, cb);
    this.starMat.uniforms.uTint.value.setRGB(cr, cg * 0.85, cb);
    this.partMat.uniforms.uTint.value.setRGB(cr * 1.05, cg * 0.9, cb);
    // Throttle the volumetric-glow gradient write to ~30Hz: it is a soft full-screen blob,
    // 30Hz is visually identical, and each write forces a full-viewport repaint.
    this._volAcc = (this._volAcc || 0) + dt;
    if (this.volRef.current && this._volAcc >= 0.032) {
      this._volAcc = 0;
      const cx = 50 + Math.sin(t * 0.19) * 14 + (p - 0.5) * 30;
      const cy = 46 + Math.cos(t * 0.15) * 11 - S(0.72, 0.9) * 16;
      const inten = 0.18 + 0.12 * Math.sin(t * 0.55) + melt * 0.26;
      this.volRef.current.style.background = 'radial-gradient(58% 54% at ' + cx.toFixed(1) + '% ' + cy.toFixed(1) + '%, rgba(' + Math.round(col[0]) + ',' + Math.round(col[1]) + ',' + Math.round(col[2]) + ',' + inten.toFixed(3) + '), rgba(0,0,0,0) 70%)';
    }
    // nebula: per-sprite spin + parallax + scroll drift
    for (let i = 0; i < this.nebula.length; i++) { const s = this.nebula[i]; s.material.rotation += dt * (0.09 + s.userData.spin * 1.4); }
    this.nebulaGroup.rotation.z += dt * 0.05;
    this.nebulaGroup.position.x = -Math.sin(az) * 3 + Math.sin(t * 0.06) * 3.2;
    this.nebulaGroup.position.y = Math.sin(t * 0.05) * 3 - p * 10;

    // ---- motion blur ---- (desktop only; on mobile it makes 3D read blurry while scrolling)
    if (this.canvasRef.current) {
      const mb = (this.reduce || this.isMobile) ? 0 : this.clamp(vel * 1000, 0, 7);
      this.canvasRef.current.style.filter = mb > 0.4 ? 'blur(' + mb.toFixed(2) + 'px)' : 'none';
    }

    // ---- copy reveals ----
    this.reveal(this.s1Ref, S(0.1, 0.18) - S(0.24, 0.3), S(0.1, 0.18));
    this.reveal(this.s2Ref, S(0.48, 0.55) - S(0.6, 0.66), S(0.48, 0.55));
    this.reveal(this.s3Ref, S(0.82, 0.88), S(0.82, 0.88));
    if (this.hintRef.current) this.hintRef.current.style.opacity = (1 - this.S(0.02, 0.07, p)).toFixed(3);
    if (this.progRef.current) this.progRef.current.style.width = (this.clamp(this.targetP, 0, 1) * 100).toFixed(2) + '%';

    this.renderer.render(this.scene, this.cam);
  }

  reveal(ref, op, enter) {
    const el = ref.current; if (!el) return;
    op = Math.max(0, op);
    el.style.opacity = op.toFixed(3);
    el.style.pointerEvents = op > 0.5 ? 'auto' : 'none';
    const inner = el.firstElementChild;
    if (inner) inner.style.transform = 'translateY(' + ((1 - enter) * 40).toFixed(1) + 'px)';
  }


  }

  function boot() {
    var el = document.querySelector('[data-hero-voyage]');
    if (!el || el.__heroBooted) return;
    el.__heroBooted = true;
    try { new HeroVoyage(el); }
    catch (e) { console.error('hero init', e); var fb = el.querySelector('[data-hero-fallback]'); if (fb) fb.style.display = 'flex'; }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
